import React, { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowRight, Play, Sparkles } from "lucide-react";
import { PROFILE, STATS, HERO_VIDEO, HERO_POSTER } from "@/lib/portfolioData";

const AnimatedNumber = ({ value, label, delay = 0 }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let started = false;

    // Parse number and suffix from value string like "10M+", "6000+", "2023+"
    const match = value.match(/^([\d.]+)([A-Za-z+]*)$/);
    const num = match ? parseFloat(match[1]) : 0;
    const suffix = match ? match[2] : "";

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            started = true;
            setTimeout(() => {
              const start = performance.now();
              const duration = 1600;
              const tick = (t) => {
                const p = Math.min((t - start) / duration, 1);
                const eased = 1 - Math.pow(1 - p, 3);
                const current = num * eased;
                let formatted;
                if (num >= 1000 && suffix === "+") {
                  formatted = Math.floor(current).toLocaleString();
                } else if (num % 1 === 0) {
                  formatted = Math.floor(current).toString();
                } else {
                  formatted = current.toFixed(1);
                }
                setDisplay(formatted + suffix);
                if (p < 1) requestAnimationFrame(tick);
              };
              requestAnimationFrame(tick);
            }, delay);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, delay]);

  return (
    <div ref={ref} className="flex flex-col" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <span className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight">
        {display}
      </span>
      <span className="text-[11px] md:text-xs uppercase tracking-[0.22em] text-[#B3B3B3] mt-2 max-w-[180px]">
        {label}
      </span>
    </div>
  );
};

export default function Hero() {
  const scrollNext = () => {
    document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      className="relative min-h-screen w-full overflow-hidden"
      data-testid="hero-section"
    >
      {/* Animated poster background (fallback + always visible) */}
      <div
        className="absolute inset-0 ken-burns"
        style={{
          backgroundImage: `url(${HERO_POSTER})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      {/* Cinematic looping video background (renders over image when loaded) */}
      <video
        className="absolute inset-0 w-full h-full object-cover"
        src={HERO_VIDEO}
        poster={HERO_POSTER}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
      />
      <div className="hero-overlay" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-40 md:pt-44 pb-20 md:pb-16 min-h-screen flex flex-col justify-between">
        {/* Top row: section tag + portrait chip */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-[1px] bg-[#D4AF37]" />
            <span className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-[#D4AF37] font-display">
              Portfolio / 2026
            </span>
          </div>
          <div className="hidden md:flex items-center gap-3 glass px-4 py-2 rounded-full">
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-70 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D4AF37]" />
            </span>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/80 font-display">
              Available for Collaborations
            </span>
          </div>
        </div>

        {/* Main content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mt-16 md:mt-24">
          <div className="lg:col-span-8 relative">
            <p className="text-[#D4AF37] text-xs md:text-sm uppercase tracking-[0.35em] font-display mb-6">
              {PROFILE.titles.join("  •  ")}
            </p>
            <h1
              className="font-display font-bold uppercase leading-[0.9] tracking-tighter text-white"
              style={{ fontSize: "clamp(2.75rem, 8vw, 7.5rem)" }}
              data-testid="hero-headline"
            >
              Capturing
              <br />
              Stories.
              <br />
              <span className="text-gradient-gold gold-heading font-serif-display italic font-medium">
                Creating Impact.
              </span>
              <br />
              Inspiring Millions.
            </h1>
            <p className="mt-8 max-w-xl text-[#B3B3B3] text-base md:text-lg leading-relaxed">
              I turn travel experiences and everyday moments into short-form cinematic
              stories that earn 10M+ organic views a month — for brands, tourism boards,
              and creators who care about craft.
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-10">
              <button
                onClick={() => document.getElementById("portfolio")?.scrollIntoView({ behavior: "smooth" })}
                className="magnetic-btn solid"
                data-testid="hero-cta-work"
              >
                <Play className="w-4 h-4" fill="currentColor" />
                View My Work
              </button>
              <button
                onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
                className="magnetic-btn"
                data-testid="hero-cta-collab"
              >
                Let's Work Together
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Right arched portrait */}
          <div className="lg:col-span-4 hidden lg:block">
            <div className="arched relative aspect-[3/4] max-w-[300px] ml-auto">
              <img
                src={PROFILE.portrait}
                alt={PROFILE.name}
                className="w-full h-full object-cover"
                loading="eager"
                data-testid="hero-portrait"
              />
              <div className="absolute bottom-4 left-4 right-4 glass rounded-lg p-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/80 font-display">
                  {PROFILE.name}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom stats */}
        <div className="mt-16 md:mt-20">
          <div className="grid grid-cols-3 gap-6 md:gap-16 pt-8 border-t border-white/10">
            {STATS.map((s, i) => (
              <AnimatedNumber key={s.label} value={s.value} label={s.label} delay={i * 150} />
            ))}
          </div>
          <button
            onClick={scrollNext}
            className="mt-10 flex items-center gap-3 text-white/60 hover:text-[#D4AF37] transition-colors group"
            data-testid="hero-scroll-indicator"
          >
            <div className="relative w-6 h-10 border border-white/25 rounded-full flex items-start justify-center pt-2">
              <div className="w-1 h-2 bg-[#D4AF37] rounded-full scroll-dot" />
            </div>
            <span className="text-[10px] uppercase tracking-[0.35em] font-display">Scroll to explore</span>
            <ArrowDown className="w-3.5 h-3.5 group-hover:translate-y-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
}
